import { formatStatNum, formatUsd } from "@bridgesplit/utils";
import { Column, Row, Table, TableColumn, Text, createRows } from "@bridgesplit/ui";
import { BsMetaUtil, RoleView } from "@bridgesplit/abf-react";
import { COPY } from "app/constants";

import { MarketStats } from "./types";
import { BorrowCapWarning, getMarketPath, TokenImage } from "../../common";
import MarketDetailCta from "./MarketDetailCta";
import { NetBorrowApy, NetLendApy } from "./common";

export default function PrincipalMarketsTable({
    marketStats,
    view
}: {
    marketStats: MarketStats[] | undefined;
    view: RoleView;
}) {
    return (
        <Table
            onRowNavigatePath={({ data }) => getMarketPath({ token: data.metadata, view })}
            paginationProps={{
                pageSize: 10,
                type: "managed",
                defaultSortKey: "supplied"
            }}
            emptyPlaceholder="No markets found"
            loading={!marketStats}
            rows={createRows(marketStats, (s) => s.metadata.mint)}
            columns={getColumns(view, marketStats)}
        />
    );
}

const getColumns = (view: RoleView, marketStats: MarketStats[] | undefined): TableColumn<MarketStats>[] => [
    {
        dataIndex: "token",
        title: "Token",
        render: ({ data: { metadata } }) => (
            <Row spacing={1}>
                <TokenImage metadata={metadata} size="md" />
                <Text>{BsMetaUtil.getSymbol(metadata)}</Text>
            </Row>
        ),
        sortBy: ({ data: { metadata } }) => BsMetaUtil.getSymbol(metadata)
    },
    view === RoleView.Borrow
        ? {
              align: "right",
              dataIndex: "borrowAPY",
              title: "Net Borrow APY",
              width: "20%",
              tooltip: COPY.PRINCIPAL_MARKETS_NET_BORROW_APY_TOOLTIP,

              render: ({ data }) => <NetBorrowApy data={data} />,
              sortBy: ({ data }) => data.wAvgBorrowApy ?? 0
          }
        : {
              align: "right",
              dataIndex: "lendApy",
              title: "Avg. Lend APY",
              width: "20%",
              tooltip: COPY.PRINCIPAL_MARKETS_NET_LEND_APY_TOOLTIP,

              render: ({ data }) => <NetLendApy data={data} />,
              sortBy: ({ data }) => data.wAvgLendApy ?? 0
          },

    {
        dataIndex: "supplied",
        title: "Supplied",
        align: "right",
        render: ({ data: { available, availableUsd, borrowed, borrowedUsd } }) => (
            <Column alignItems="flex-end">
                <Text color="body" translate="no">
                    {formatStatNum(available + borrowed)}
                </Text>
                <Text
                    loading={availableUsd === undefined || borrowedUsd === undefined}
                    variant="body2"
                    color="caption"
                    translate="no"
                >
                    {formatUsd((availableUsd ?? 0) + (borrowedUsd ?? 0))}
                </Text>
            </Column>
        ),
        sortBy: ({ data: { availableUsd, borrowedUsd } }) => (availableUsd ?? 0) + (borrowedUsd ?? 0)
    },
    {
        dataIndex: "borrowed",
        title: "Borrowed",
        align: "right",
        render: ({ data: { borrowed, borrowedUsd, borrowCapWarning } }) => (
            <Column alignItems="flex-end">
                <BorrowCapWarning borrowCapWarning={borrowCapWarning}>{formatStatNum(borrowed ?? 0)}</BorrowCapWarning>

                <Text
                    loadingWidth="50px"
                    loading={borrowedUsd === undefined}
                    variant="body2"
                    color="caption"
                    translate="no"
                >
                    {formatUsd(borrowedUsd)}
                </Text>
            </Column>
        ),
        sortBy: ({ data: { borrowedUsd } }) => borrowedUsd || 0
    },

    {
        dataIndex: "actions",
        title: "",
        align: "right",
        render: ({ data: { metadata } }) => <MarketDetailCta token={metadata} view={view} />
    }
];
